import { Languages } from 'lucide-react';
import { useContext } from 'react';

import { LanguageContext, type Language } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

const OPTIONS: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'sw', label: 'Kiswahili' },
];

/**
 * LanguageSwitcher — choose English or Kiswahili.
 *
 * Reads/writes the LanguageProvider. The choice is persisted (localStorage) and
 * applied immediately. Benign UI preference — not auth state. Reads the context
 * directly (with a safe default) so it can also render in isolation/tests
 * without a provider, mirroring `useTranslation`.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const ctx = useContext(LanguageContext);
  const lang: Language = ctx?.lang ?? 'en';
  const setLang = ctx?.setLang ?? (() => undefined);

  return (
    <div className={cn('inline-flex items-center gap-2', className)} data-slot="language-switcher">
      <Languages className="size-4 text-[var(--text-secondary)]" aria-hidden />
      <label htmlFor="language-select" className="sr-only">
        Language
      </label>
      <select
        id="language-select"
        aria-label="Language"
        value={lang}
        onChange={(e) => setLang(e.target.value as Language)}
        className="min-h-[44px] rounded-md border border-[var(--border-default)] bg-[var(--bg-input)] px-3 text-sm text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-accent)]"
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
